import { FC, PropsWithChildren, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";
import { deduplicateArray } from "@/lib/deduplicate-array";

import { User } from "../models/user-model";

import { UserSelectDropdownItem } from "./user-select-dropdown-item";

interface UserMultiselectDropdownProps extends PropsWithChildren {
  activeUserList: User[];
  value?: User[] | null;
  onValueChange?: (userList: User[]) => void;
  asChild?: boolean;
  isReadOnly?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: "top" | "right" | "bottom" | "left";
}

export const UserMultiselectDropdown: FC<UserMultiselectDropdownProps> = ({
  children,
  asChild,
  value,
  onValueChange,
  activeUserList,
  isReadOnly,
  open,
  onOpenChange,
  side,
}) => {
  const { t } = useTranslation("common");
  const comboboxOptions = useMemo(() => {
    const options: ComboboxOption<User>[] = activeUserList
      .slice()
      .sort((a, b) => {
        const _a = a.username ?? "";
        const _b = b.username ?? "";
        return _a.localeCompare(_b);
      })
      .map((user) => ({
        id: user.id,
        value: `${user.username ?? ""} ; ${user.fullname ?? ""} ; ${user.email ?? ""}`,
        original: user,
      }));

    return options;
  }, [activeUserList]);

  const renderItem = (option: ComboboxOption<User>): ReactElement => {
    const selected = value?.some((user) => user.id === option.id);
    const user = option.original ?? undefined;
    return <UserSelectDropdownItem key={option.id} user={user} selected={selected} />;
  };

  const renderHeader = () => {
    const isDisabled = !comboboxOptions.length || !!isReadOnly;
    return (
      <div className="flex flex-row items-center justify-start gap-3 pl-3 pr-4 pt-0.5 text-xs text-muted-foreground">
        <Button
          variant="link"
          size="xs"
          disabled={isDisabled}
          className="h-auto p-0 text-xs font-normal text-muted-foreground no-underline hover:text-foreground hover:no-underline"
          onClick={() => {
            if (isDisabled || !onValueChange) {
              return;
            }
            onValueChange(activeUserList);
          }}
        >
          Alle
        </Button>
        <Button
          variant="link"
          size="xs"
          disabled={isDisabled}
          className="h-auto p-0 text-xs font-normal text-muted-foreground no-underline hover:text-foreground hover:no-underline"
          onClick={() => {
            if (isDisabled || !onValueChange) {
              return;
            }
            onValueChange([]);
          }}
        >
          Keine
        </Button>
      </div>
    );
  };

  const handleSelected = (selectedOption: ComboboxOption<User> | null) => {
    if (!comboboxOptions.length || isReadOnly) {
      return;
    }

    if (!selectedOption?.original) {
      return;
    }

    const isDeselect = value?.some((user) => user.id === selectedOption.id);

    if (isDeselect) {
      const newSelectedUserList = value?.filter((user) => user.id !== selectedOption.id) ?? [];

      if (onValueChange) {
        onValueChange(newSelectedUserList);
      }

      return;
    }

    const newSelectedUserList = deduplicateArray([...(value ?? []), selectedOption.original], (user) => user.id);
    if (onValueChange) {
      onValueChange(newSelectedUserList);
    }
  };

  return (
    <Combobox
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleSelected}
      placeholder={t("selectPlaceholders.assignees")}
      side={side}
      renderHeader={renderHeader}
      open={open}
      onOpenChange={onOpenChange}
      closeOnSelect={false}
      allowDeselect={true}
      isReadOnly={isReadOnly}
      disabled={!comboboxOptions.length || !!isReadOnly}
    >
      {children}
    </Combobox>
  );
};
